<template>
  <div class="row">
    <div
      v-click-outside="hideDropdown"
    >
      <div
        class="scroll-dropdown__trigger"
        :class="{ isActive: dropdown, disabled: disabled }"
        @click="showDropdown"
      >
        <div
          class="truncate dropdown-display"
          :title="displaySelected"
        >
          {{ displaySelected }}
        </div>
      </div>
      <div
        v-if="dropdown"
        class="scroll-dropdown__dropdown"
      >
        <OverlayScrollbars
          ref="scrollbar"
          class="scroll-dropdown__filters-wrap"
          :options="{ callbacks: { onUpdated: setScrollPosition } }"
        >
          <ul>
            <li
              v-for="(item, index) in pickOptions"
              :key="`${index}`"
              @click="handleSelect(item)"
            >
              <RsIcon
                :icon="isItemSelected(item) ? 'check' : ''"
                weight="fas"
                class="ml-2"
              />
              <label class="m-0 scroll-dropdown__check-wrap pr-3 pl-2 mb-1">{{
                isPlainOptions ? item : item.text
              }}</label>
            </li>
          </ul>
        </OverlayScrollbars>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
import { defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';

type TDropdownPosition = 'bottom' | 'top';

export default defineComponent({
  name: 'RsTimePicker',
  props: {
    /**
     * increment of time interval in minutes.
     */
    increment: {
      type: Number,
      default: 30,
    },
    /**
     * select options for the dropdown
     */
    pickOptions: {
      type: Array as PropType<Array<string | ISelectOption | null>>,
      default: () => [],
    },
    isPlainOptions: {
      type: Boolean,
      default: true,
    },
    /**
     * selected value of the dropdown
     */
    selected: {
      type: [Object, String] as PropType<ISelectOption | string | null>,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
    /**
     * flag for the state of dropdown
     */
      dropdown: false,
      dropdownPosition: 'bottom' as TDropdownPosition,
    };
  },
  computed: {
    selectIndex(): number {
      if (this.isPlainOptions) {
        return this.pickOptions.indexOf(this.selected);
      }
      return this.pickOptions.findIndex(
        (item) => item != null && (item as ISelectOption).text === this.selected,
      );
    },
    displaySelected(): string {
      if (this.isPlainOptions || this.selected == null) return (this.selected as string) ?? '';
      const found = this.pickOptions.find(
        (item) => item != null && (item as ISelectOption).value === this.selected,
      );
      return found ? (found as ISelectOption).text : (this.selected as string) ?? '';
    },
  },
  methods: {
    /** Checks whether the given item matches the currently selected value. */
    isItemSelected(item: string | ISelectOption | null): boolean {
      if (this.isPlainOptions) {
        return this.selected === item;
      }
      return item != null && (item as ISelectOption).value === this.selected;
    },
    handleSelect(item: string | ISelectOption | null): void {
      this.$emit('onselect', this.isPlainOptions ? item : (item as ISelectOption).value);
      this.dropdown = false;
    },
    /**
     * Setting up the scroll position of the dropdown
     * by getting the scrollbar instance and index
     * of selected item
     */
    setScrollPosition() {
      const scrollbar = (this.$refs.scrollbar as OverlayScrollbarsComponent);
      const osInstance = scrollbar.osInstance();
      if (osInstance && this.selectIndex !== -1) {
        const el = scrollbar.$el as HTMLElement;
        const selectedLi = el.querySelectorAll('li')[this.selectIndex];
        if (selectedLi) {
          const offset = Math.max(0, selectedLi.offsetTop - selectedLi.offsetHeight * 2);
          osInstance.scroll({
            y: offset,
          });
        }
      }
    },

    showDropdown(): void {
      if (this.disabled) return;
      this.dropdown = !this.dropdown;
    },
    hideDropdown(): void {
      this.dropdown = false;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.scroll-dropdown {
  position: relative;
  margin-bottom: 1rem;
  max-width: 440px;
  width: 100%;

  .container {
    background-color: #efefef;
    display: block;
    left: 23px;
    margin-top: 2px;
    width: 5px;
    height: 10px;
    border: solid #dedede;
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }

  .label {
    margin: 0 0 0 0.5rem;
    font-weight: normal;
    color: $tertiary-muted;
    display: inline;
  }
  .center {
    align-items: center;
    cursor: pointer;
  }

  &__trigger {
    width: 100px;
    height: 30px;
    border-radius: 2px;
    margin: 0 8px;
    font-size: 0.9375rem;
    padding: 0.5rem 0.5rem 0.5rem 0rem;
    cursor: pointer;
    align-items: center;
    transition: background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    .spin {
      padding: 0;
      transition: transform 0.2s ease;
    }
    &.isActive {
      outline: 0;
      border: 3px solid $secondary-color;
      border-style: none none solid none;
      .spin {
        transform: rotate(180deg);
      }
    }
  }
  &__dropdown {
    position: absolute;
    margin-top: 0.8rem;
    z-index: 110;
    border: 1px solid $tertiary-gray;
    background-color: #fff;
    visibility: inherit;
    opacity: 1;
    box-shadow: 0px 3px 4px 1px rgba(0, 0, 0, 0.11);
    border-radius: $global-radius;
    overflow: hidden;
    &:after,
    &:before {
      position: absolute;
      content: "";
      top: 0;
      display: block;
      height: 4px;
      z-index: 1;
    }
  }
  &__filters-wrap {
    ul {
      padding: 0.5rem 0;
      li {
        display: block;
        margin: 0;
        &:hover {
          cursor: pointer;
          border-radius: 5px;
          margin: 0;
          background-color: rgba($secondary-color, 0.1) !important;
        }
      }
    }
    height: 200px;
    background-color: #efefef;
    border-bottom: 1px solid #dedede;
    border-top: 1px solid #dedede;
    max-height: 200px;
  }
  &__check-wrap {
    position: relative;
    padding-left: 1rem;
    font-weight: 400;
    color: black;
  }
}
</style>
